import type { AccusedRecord } from '../types/criminalCaseRequest';
import type {
  AttorneyGeneralSaveStep1,
  AttorneyGeneralSaveStep2,
  AttorneyGeneralSaveStep3,
  AttorneyGeneralSaveStep4,
  AttorneyGeneralSaveStep5,
  AttorneyGeneralSaveStep6,
  AttorneyGeneralSaveStep7,
  AttorneyGeneralSaveStep8,
  AttorneyGeneralSaveStep9,
  AttorneyGeneralSaveStep10,
  AttorneyGeneralSaveStep12,
  AttorneyGeneralStepFile,
  AttorneyGeneralStepsResponse,
  SaveAttorneyGeneralStepsPayload,
} from '../types/attorneyGeneralApi';
import type {
  AttorneyGeneralInlineUpload,
  AttorneyGeneralWizardDraft,
} from '../types/attorneyGeneralCaseRequest';
import { calcAgeFromDob } from '../utils/caseFieldValidation';
import { isDocumentTypeUuid, isPredefinedDocumentType, resolveDocumentTypeUuid } from '../utils/documentTypeId';
import { caseRequestIdForSave, draftResumeStepFromApi, finalizeCaseRequestSavePayload, SaveStepFields, strOrNull } from '../utils/caseRequestPayload';
import { looksLikeStorageKey, normalizeDocumentFileFields } from '../utils/fileDisplayName';
import type { AttorneyGeneralWizardStep } from '../pages/wizard/attorneyGeneral/attorneyGeneralConstants';
import {
  AGD_INTERNAL_CATEGORY_ID,
  ATTORNEY_GENERAL_CATEGORY_ID,
  FILE_CATEGORY_AGD_INTERNAL,
  FILE_CATEGORY_ATTORNEY_GENERAL,
  isOtherCourtType,
} from '../pages/wizard/attorneyGeneral/attorneyGeneralConstants';
import { isOtherNextStep, requiresCourtType } from '../pages/wizard/attorneyGeneral/attorneyGeneralHelpers';
import { parseApplicableAreasFromApi } from './criminalCaseMapper';

export type { AttorneyGeneralWizardStep };

export function caseRequestIdFromResponse(
  response: Pick<AttorneyGeneralStepsResponse, 'case_request_id' | 'caseRequestId'>,
): string {
  const id = response.case_request_id ?? response.caseRequestId;
  return typeof id === 'string' ? id.trim() : '';
}

function pickStr(...values: (string | null | undefined)[]): string {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function step1AdviceFiles(s1: AttorneyGeneralSaveStep1 | undefined): AttorneyGeneralStepFile[] {
  return s1?.previous_advice_documents ?? s1?.previous_advice_files ?? [];
}

function step1JudgementFiles(s1: AttorneyGeneralSaveStep1 | undefined): AttorneyGeneralStepFile[] {
  return (
    s1?.judgment_or_order_documents ??
    s1?.judgement_order_documents ??
    s1?.judgement_order_files ??
    []
  );
}

export function referenceNoFromResponse(response: AttorneyGeneralStepsResponse): string | undefined {
  const ref = response.reference_no ?? response.referenceNo ?? response.fileReferenceNumber;
  const t = typeof ref === 'string' ? ref.trim() : '';
  return t || undefined;
}


function step1LooksComplete(s1: AttorneyGeneralSaveStep1 | undefined): boolean {
  return Boolean((s1?.sub_category_id ?? '').trim());
}

function step2LooksComplete(s2: AttorneyGeneralSaveStep2 | undefined): boolean {
  if (!s2) return false;
  if (typeof s2.is_incorporated_body === 'boolean') return true;
  return Boolean(
    (s2.plaintiff_applicant_name ?? '').trim() ||
      (s2.party_body_type ?? s2.unincorporated_body_type_id ?? s2.incorporated_body_type_id ?? '')
        .toString()
        .trim(),
  );
}

function step3LooksComplete(_s3: AttorneyGeneralSaveStep3 | undefined): boolean {
  return true;
}

function step4LooksComplete(_s4: AttorneyGeneralSaveStep4 | undefined): boolean {
  return true;
}

function step5LooksComplete(_s5: AttorneyGeneralSaveStep5 | undefined): boolean {
  return true;
}

function step6LooksComplete(_s6: AttorneyGeneralSaveStep6 | undefined): boolean {
  return true;
}

function step7LooksComplete(_s7: AttorneyGeneralSaveStep7 | undefined): boolean {
  return true;
}

function step8LooksComplete(_s8: AttorneyGeneralSaveStep8 | undefined): boolean {
  return true;
}

function step9LooksComplete(_s9: AttorneyGeneralSaveStep9 | undefined): boolean {
  return true;
}

function step10LooksComplete(_s10: AttorneyGeneralSaveStep10 | undefined): boolean {
  return true;
}

function step12LooksComplete(s12: AttorneyGeneralSaveStep12 | undefined): boolean {
  const files = step12DocumentsFromApi(s12);
  return files.some(
    (f) =>
      Boolean((f.file_name ?? '').trim() || (f.original_file_name ?? '').trim()) ||
      Boolean((f.reason ?? '').trim()),
  );
}

export function resolveAttorneyGeneralDraftResumeStep(
  response: AttorneyGeneralStepsResponse,
): AttorneyGeneralWizardStep {
  return draftResumeStepFromApi(
    response,
    () => {
  const checks: [boolean | undefined, AttorneyGeneralWizardStep][] = [
    [response.step1Processed, 1],
    [response.step2Processed, 2],
    [response.step3Processed, 3],
    [response.step4Processed, 4],
    [response.step5Processed, 5],
    [response.step6Processed, 6],
    [response.step7Processed, 7],
    [response.step8Processed, 8],
    [response.step9Processed, 9],
    [response.step10Processed, 10],
    [response.step12Processed, 12],
  ];
  for (const [processed, step] of checks) {
    if (processed === false) {
      if (step === 12 && response.step10Processed !== false) return 11;
      return step;
    }
  }

  if (!step1LooksComplete(response.step_1)) return 1;
  if (!step2LooksComplete(response.step_2)) return 2;
  if (!step3LooksComplete(response.step_3)) return 3;
  if (!step4LooksComplete(response.step_4)) return 4;
  if (!step5LooksComplete(response.step_5)) return 5;
  if (!step6LooksComplete(response.step_6)) return 6;
  if (!step7LooksComplete(response.step_7)) return 7;
  if (!step8LooksComplete(response.step_8)) return 8;
  if (!step9LooksComplete(response.step_9)) return 9;
  if (!step10LooksComplete(response.step_10)) return 10;
  if (!step12LooksComplete(response.step_12)) return 11;
  return 12;
    },
    (step) => step >= 1 && step <= 12,
  );
}

function yesNoToBool(v: '' | 'yes' | 'no'): boolean | undefined {
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return undefined;
}

function boolToYesNo(v: boolean | undefined | null): '' | 'yes' | 'no' {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

function inlineUploadsToApi(uploads: AttorneyGeneralInlineUpload[]): AttorneyGeneralStepFile[] {
  return uploads
    .filter((u) => strOrNull(u.fileName) || strOrNull(u.originalFileName))
    .map((u) => {
      const a = u.fileName.trim();
      const b = u.originalFileName.trim();
      if (a && looksLikeStorageKey(a)) {
        return { file_name: a, ...(b ? { original_file_name: b } : {}) };
      }
      if (b && looksLikeStorageKey(b)) {
        return { file_name: b, ...(a ? { original_file_name: a } : {}) };
      }
      return { file_name: a, ...(b ? { original_file_name: b } : {}) };
    });
}

function inlineUploadsFromApi(files: AttorneyGeneralStepFile[] | undefined): AttorneyGeneralInlineUpload[] {
  if (!files?.length) return [];
  return files
    .filter((f) => (f.file_name ?? '').trim() || (f.original_file_name ?? '').trim())
    .map((f) => {
      const { fileName, originalFileName } = normalizeDocumentFileFields(
        f.file_name ?? '',
        f.original_file_name,
      );
      return {
        id: (f.case_request_document_id ?? '').trim() || crypto.randomUUID(),
        fileName,
        originalFileName,
        fileSize: f.file_size,
        uploadTime: f.upload_time ?? '',
      };
    });
}

function step12DocumentsFromApi(s12: AttorneyGeneralSaveStep12 | undefined): AttorneyGeneralStepFile[] {
  return [
    ...(s12?.uploaded_files ?? []),
    ...(s12?.extra_uploaded_files ?? []),
    ...(s12?.documents ?? []),
  ];
}

function apiDocumentTypeId(file: AttorneyGeneralStepFile): string {
  return (file.document_type_id ?? file.document_id ?? '').trim();
}

function step12FileNamesForSave(
  fileName: string | null | undefined,
  originalFileName: string | null | undefined,
): { file_name?: string; original_file_name?: string } {
  const a = fileName?.trim() ?? '';
  const b = originalFileName?.trim() ?? '';
  if (!a && !b) return {};
  if (a && looksLikeStorageKey(a)) {
    return { file_name: a, ...(b ? { original_file_name: b } : {}) };
  }
  if (b && looksLikeStorageKey(b)) {
    return { file_name: b, ...(a ? { original_file_name: a } : {}) };
  }
  return { file_name: a, ...(b ? { original_file_name: b } : {}) };
}

function resolveApplicableAreaIds(response: AttorneyGeneralStepsResponse, prev: string[]): string[] {
  for (const src of [response.applicable_areas]) {
    const { ids } = parseApplicableAreasFromApi(src);
    if (ids.length) return ids;
  }
  if (Array.isArray(response.applicableAreaIds) && response.applicableAreaIds.length) {
    return response.applicableAreaIds.filter((id) => typeof id === 'string' && id.trim());
  }
  return prev;
}

function resolveApplicableAreaNames(response: AttorneyGeneralStepsResponse, prev: string[]): string[] {
  for (const src of [response.applicable_areas]) {
    const { names } = parseApplicableAreasFromApi(src);
    if (names.length) return names;
  }
  const named = response.applicable_area_names ?? response.applicableAreaNames;
  if (Array.isArray(named) && named.length) {
    return named.filter((n): n is string => typeof n === 'string' && Boolean(n.trim()));
  }
  return prev;
}

function partyBodyTypeFromStep2(s2: AttorneyGeneralSaveStep2 | undefined): '' | 'government' | 'incorporated' {
  if (!s2) return '';
  if (typeof s2.is_incorporated_body === 'boolean') {
    return s2.is_incorporated_body ? 'incorporated' : 'government';
  }
  if (s2.party_body_type === 'government' || s2.party_body_type === 'incorporated') {
    return s2.party_body_type;
  }
  if (s2.incorporated_body_type_id?.trim() || s2.incorporated_body_type_name?.trim()) return 'incorporated';
  if (s2.unincorporated_body_type_id?.trim() || s2.unincorporated_body_type_name?.trim()) return 'government';
  return '';
}

function accusedFromStep5(s5: AttorneyGeneralSaveStep5 | undefined, prev: AccusedRecord[]): AccusedRecord[] {
  const singleName = (s5?.suspect_name ?? '').trim();
  if (singleName) {
    return [
      {
        id: crypto.randomUUID(),
        name: singleName,
        status: (s5?.suspect_status_id ?? '').trim(),
        statusOther: '',
      },
    ];
  }

  const fromSuspectNames = s5?.suspect_names?.length ? s5.suspect_names : [];
  if (fromSuspectNames.length) {
    return fromSuspectNames
      .filter((n) => typeof n === 'string' && n.trim())
      .map((name) => ({
        id: crypto.randomUUID(),
        name: name.trim(),
        status: s5?.suspect_status_id ?? '',
        statusOther: '',
      }));
  }

  const suspects = s5?.suspects;
  if (!Array.isArray(suspects) || suspects.length === 0) return prev;

  if (typeof suspects[0] === 'string') {
    return (suspects as string[])
      .filter((n) => typeof n === 'string' && n.trim())
      .map((name) => ({
        id: crypto.randomUUID(),
        name: name.trim(),
        status: s5?.suspect_status_id ?? '',
        statusOther: '',
      }));
  }

  return (suspects as Array<{
    id?: string;
    suspect_name?: string;
    suspect_status_id?: string;
    suspect_status_name?: string;
    suspect_status_other?: string;
  }>)
    .map((s) => ({
      id: (s.id ?? crypto.randomUUID()).toString(),
      name: (s.suspect_name ?? '').trim(),
      status: (s.suspect_status_id ?? s5?.suspect_status_id ?? '').trim(),
      statusOther: (s.suspect_status_other ?? s.suspect_status_name ?? '').trim(),
    }))
    .filter((a) => a.name);
}

function mapStep1(draft: AttorneyGeneralWizardDraft): SaveStepFields<AttorneyGeneralSaveStep1> {
  const instituted = draft.hasActionInstituted === 'yes';
  const sendCourtType = requiresCourtType(draft.hasActionInstituted);
  const previousAdviceFiles = inlineUploadsToApi(draft.previousAdviceUploads);
  const judgementFiles = inlineUploadsToApi(draft.judgementOrderUploads);

  return {
    sub_category_id: strOrNull(draft.subFileCategoryId),
    description: strOrNull(draft.description),
    province_id: strOrNull(draft.provinceId),
    district_id: strOrNull(draft.districtId),
    case_source: strOrNull(draft.caseSource),
    high_court_id: strOrNull(draft.highCourtId),
    magistrate_court_id: strOrNull(draft.magistrateCourtId),
    police_station_id: strOrNull(draft.policeStationId),
    country_involved_id: strOrNull(draft.countryInvolvedId),
    direction_of_request: strOrNull(draft.directionOfRequest),
    external_case_reference_number: strOrNull(draft.yourReferenceNumber),
    other_external_case_reference_number: strOrNull(draft.otherExternalReferences),
    has_previous_advice_received: yesNoToBool(draft.hasPreviousAdvice),
    previous_advice_documents: previousAdviceFiles.length ? previousAdviceFiles : undefined,
    has_connected_relevant_case: yesNoToBool(draft.hasConnectedAdviceCase),
    connected_case_details:
      draft.hasConnectedAdviceCase === 'yes' ? strOrNull(draft.connectedAdviceDetails) : undefined,
    has_action_instituted: yesNoToBool(draft.hasActionInstituted),
    court_type_id: sendCourtType ? strOrNull(draft.courtTypeId) : undefined,
    court_type_name: sendCourtType ? strOrNull(draft.courtTypeName) : undefined,
    other_court_type:
      sendCourtType && isOtherCourtType(draft.courtTypeId, draft.courtTypeName)
        ? strOrNull(draft.courtTypeOther)
        : undefined,
    court_id: instituted ? strOrNull(draft.courtId) : undefined,
    is_appeal_matter: yesNoToBool(draft.isAppealMatter),
    original_court: draft.isAppealMatter === 'yes' ? strOrNull(draft.originalCourt) : undefined,
    judgment_or_order_documents: judgementFiles.length ? judgementFiles : undefined,
    case_number: strOrNull(draft.caseNumber),
    has_connected_case: yesNoToBool(draft.hasConnectedMatters),
    connected_case_number:
      draft.hasConnectedMatters === 'yes' ? strOrNull(draft.connectedCaseNumber) : undefined,
    connected_agd_file_ref_available: yesNoToBool(draft.hasConnectedMatters),
    connected_agd_file_ref_number:
      draft.hasConnectedMatters === 'yes' ? strOrNull(draft.connectedAgdFileRef) : undefined,
  };
}

function mapStep2(draft: AttorneyGeneralWizardDraft): SaveStepFields<AttorneyGeneralSaveStep2> {
  const incorporated = draft.partyBodyType === 'incorporated';
  return {
    plaintiff_applicant_name: strOrNull(draft.plaintiffName),
    defendant_respondent_name: strOrNull(draft.defendantName),
    is_incorporated_body: incorporated,
    other_involved_institutions: strOrNull(draft.otherInvolvedInstitutions),
    leagel_office_or_officer_incharge_name: strOrNull(draft.legalOfficerName),
    leagel_office_or_officer_incharge_contact_no: strOrNull(draft.legalOfficerContact),
  };
}

function mapStep3(draft: AttorneyGeneralWizardDraft): SaveStepFields<AttorneyGeneralSaveStep3> {
  const otherSelected = isOtherNextStep(draft.nextStepOfCaseName, draft.nextStepOfCaseId);
  return {
    subject_of_dispute: strOrNull(draft.subjectOfDispute),
    incident_occurred_from_date: strOrNull(draft.incidentDateFrom),
    incident_occurred_to_date: strOrNull(draft.incidentDateTo),
    case_next_step_id: strOrNull(draft.nextStepOfCaseId),
    case_next_step_name: strOrNull(draft.nextStepOfCaseName),
    case_next_step_other: otherSelected ? strOrNull(draft.nextStepOfCaseOther) : null,
    case_next_date: strOrNull(draft.nextCaseDate),
  };
}

function mapStep4(draft: AttorneyGeneralWizardDraft): SaveStepFields<AttorneyGeneralSaveStep4> {
  const age = calcAgeFromDob(draft.victimDob);
  return {
    name_of_victim: strOrNull(draft.victimName),
    date_of_birth_victim: strOrNull(draft.victimDob),
    age_victim: typeof age === 'number' ? age : undefined,
    gender_id_victim: strOrNull(draft.victimGenderId),
  };
}

function mapStep5(draft: AttorneyGeneralWizardDraft): SaveStepFields<AttorneyGeneralSaveStep5> {
  const suspects = draft.accused
    .filter((a) => a.name.trim())
    .map((a) => ({
      suspect_name: a.name.trim(),
      suspect_status_id: strOrNull(a.status),
    }));
  return {
    date_of_offence_from: strOrNull(draft.offenceDateFrom),
    date_of_offence_to: strOrNull(draft.offenceDateTo),
    place_of_offence: strOrNull(draft.placeOfOffence),
    suspects: suspects.length ? suspects : undefined,
  };
}

function mapStep6(draft: AttorneyGeneralWizardDraft): SaveStepFields<AttorneyGeneralSaveStep6> {
  const age = calcAgeFromDob(draft.accusedDob);
  return {
    name_of_accused: strOrNull(draft.accusedName),
    nationality_id_accused: strOrNull(draft.nationalityId),
    country_of_origin_id_accused: strOrNull(draft.countryOfOriginId),
    date_of_birth_accused: strOrNull(draft.accusedDob),
    age_accused: typeof age === 'number' ? age : undefined,
    gender_id_accused: strOrNull(draft.accusedGenderId),
    passport_or_id_number_accused: strOrNull(draft.passportOrIdNumber),
    current_custody_status_id_accused: strOrNull(draft.custodyStatusId),
    custodial_authority_accused: strOrNull(draft.custodialAuthority),
    last_known_location_accused: strOrNull(draft.lastKnownLocation),
  };
}

function mapStep7(draft: AttorneyGeneralWizardDraft): SaveStepFields<AttorneyGeneralSaveStep7> {
  return {
    is_arrest_warrant_available: yesNoToBool(draft.arrestWarrantAvailable),
    warrant_reference:
      draft.arrestWarrantAvailable === 'yes' ? strOrNull(draft.warrantReference) : undefined,
    applicable_treaty_or_arrangement: strOrNull(draft.applicableTreatyArrangement),
  };
}

function mapStep8(draft: AttorneyGeneralWizardDraft): SaveStepFields<AttorneyGeneralSaveStep8> {
  const numRespondents = draft.numberOfRespondents.trim();
  return {
    name_of_petitioner: strOrNull(draft.petitionerName),
    nic_or_identification_number_petitioner: strOrNull(draft.petitionerNic),
    address_petitioner: strOrNull(draft.petitionerAddress),
    contact_no_petitioner: strOrNull(draft.petitionerContact),
    email_address_petitioner: strOrNull(draft.petitionerEmail),
    is_represented_by_attorney: yesNoToBool(draft.representedByAttorney),
    attorney_name: draft.representedByAttorney === 'yes' ? strOrNull(draft.attorneyName) : null,
    attorney_contact_number: draft.representedByAttorney === 'yes' ? strOrNull(draft.attorneyContact) : null,
    respondent_names: strOrNull(draft.respondentNames),
    respondent_type_id: strOrNull(draft.respondentType),
    respondent_type_name: strOrNull(draft.respondentTypeName),
    respondent_address: strOrNull(draft.respondentAddress),
    number_of_respondents: numRespondents ? numRespondents : undefined,
  };
}

function mapStep9(draft: AttorneyGeneralWizardDraft): SaveStepFields<AttorneyGeneralSaveStep9> {
  const accessIds = draft.rtiPreferredMethodOfAccessIds.filter(Boolean);
  return {
    requestor_name: strOrNull(draft.rtiRequestorName),
    nic_or_passport_number: strOrNull(draft.rtiNicOrPassport),
    requestor_address: strOrNull(draft.rtiAddress),
    requestor_contact_number: strOrNull(draft.rtiContactNumber),
    requestor_email_address: strOrNull(draft.rtiEmailAddress),
    is_requestor_sri_lankan: yesNoToBool(draft.rtiIsSriLankanCitizen),
    specific_public_authority_name: strOrNull(draft.rtiSpecificPublicAuthorityName),
    requested_information_details: strOrNull(draft.rtiRequestedInformationDetails),
    information_requested_time_period: strOrNull(draft.rtiInformationRequestedTimePeriod),
    preferred_method_of_access_ids: accessIds.length ? accessIds : undefined,
    preferred_language_id: strOrNull(draft.rtiPreferredLanguageId),
    involves_life_or_personal_liberty: yesNoToBool(draft.rtiInvolvesLifeOrPersonalLiberty),
    justification:
      draft.rtiInvolvesLifeOrPersonalLiberty === 'yes' ? strOrNull(draft.rtiJustification) : null,
    additional_notes: strOrNull(draft.rtiAdditionalNotes),
  };
}

function mapStep10(draft: AttorneyGeneralWizardDraft): SaveStepFields<AttorneyGeneralSaveStep10> {
  return {
    urgency_level_id: strOrNull(draft.urgencyLevelId),
    deadline: strOrNull(draft.deadline),
    urgency_justification: strOrNull(draft.urgencyJustification),
  };
}

function mapStep12(draft: AttorneyGeneralWizardDraft): SaveStepFields<AttorneyGeneralSaveStep12> {
  const uploaded_files = draft.documents
    .filter(
      (d) =>
        isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (strOrNull(d.fileName) || strOrNull(d.skipReason)),
    )
    .map((d) => {
      const reason = strOrNull(d.skipReason);
      const names = step12FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      const documentTypeId = resolveDocumentTypeUuid(d.documentTypeId, d.checklistItemId);
      return {
        ...(documentTypeId ? { document_type_id: documentTypeId } : {}),
        ...names,
        ...(reason ? { reason } : {}),
      };
    });
  const extra_uploaded_files = draft.documents
    .filter(
      (d) =>
        !isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (strOrNull(d.fileName) || strOrNull(d.skipReason)),
    )
    .map((d) => {
      const reason = strOrNull(d.skipReason);
      const names = step12FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      return {
        ...names,
        ...(reason ? { reason } : {}),
      };
    });

  return {
    ...(uploaded_files.length ? { uploaded_files } : {}),
    ...(extra_uploaded_files.length ? { extra_uploaded_files } : {}),
  };
}

const ATTORNEY_GENERAL_SAVE_STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12] as const;

const STEP_MAPPERS: Record<number, (draft: AttorneyGeneralWizardDraft) => SaveStepFields<unknown>> = {
  1: mapStep1,
  2: mapStep2,
  3: mapStep3,
  4: mapStep4,
  5: mapStep5,
  6: mapStep6,
  7: mapStep7,
  8: mapStep8,
  9: mapStep9,
  10: mapStep10,
  12: mapStep12,
};

export function buildSaveAttorneyGeneralPayload(
  draft: AttorneyGeneralWizardDraft,
  options: {
    currentStep?: AttorneyGeneralWizardStep;
    isDraft: boolean;
    confirmedCorrect?: boolean;
    allSteps?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): SaveAttorneyGeneralStepsPayload {
  const areaIds = draft.applicableAreaIds.filter(Boolean);
  const payload: SaveAttorneyGeneralStepsPayload = {
    is_draft_status: options.isDraft,
  };
  const caseRequestId = caseRequestIdForSave(draft.draftRequestId);
  if (caseRequestId) payload.case_request_id = caseRequestId;

  if (options.isUpdateAlreadySubmitted) payload.is_update_already_submitted = true;
  if (options.confirmedCorrect != null) payload.confirmed_correct = options.confirmedCorrect;
  if (caseRequestId || areaIds.length) payload.applicable_areas = areaIds.length ? areaIds : null;

  if (options.allSteps) {
    for (const stepNum of ATTORNEY_GENERAL_SAVE_STEPS) {
      const key = `step_${stepNum}` as keyof SaveAttorneyGeneralStepsPayload;
      (payload as Record<string, unknown>)[key] = STEP_MAPPERS[stepNum](draft);
    }
    return finalizeCaseRequestSavePayload(payload, options);
  }

  const step = options.currentStep;
  if (!step) return finalizeCaseRequestSavePayload(payload, options);

  if (options.isDraft) {
    for (const stepNum of ATTORNEY_GENERAL_SAVE_STEPS) {
      if (stepNum > step) break;
      const key = `step_${stepNum}` as keyof SaveAttorneyGeneralStepsPayload;
      (payload as Record<string, unknown>)[key] = STEP_MAPPERS[stepNum](draft);
    }
    return finalizeCaseRequestSavePayload(payload, options);
  }

  const mapper = STEP_MAPPERS[step];
  if (!mapper) return finalizeCaseRequestSavePayload(payload, options);

  const key = `step_${step}` as keyof SaveAttorneyGeneralStepsPayload;
  (payload as Record<string, unknown>)[key] = mapper(draft);
  return finalizeCaseRequestSavePayload(payload, options);
}

export function mapAttorneyGeneralStepsToDraft(
  response: AttorneyGeneralStepsResponse,
  prev: AttorneyGeneralWizardDraft,
): AttorneyGeneralWizardDraft {
  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;
  const s5 = response.step_5;
  const s6 = response.step_6;
  const s7 = response.step_7;
  const s8 = response.step_8;
  const s9 = response.step_9;
  const s10 = response.step_10;
  const s12 = response.step_12;

  const documents = (() => {
    const fromApi = step12DocumentsFromApi(s12);
    if (!fromApi.length) return prev.documents;
    if (!prev.documents.length) {
      return fromApi
        .filter((f) => {
          const typeId = apiDocumentTypeId(f);
          return typeId
            ? isDocumentTypeUuid(typeId)
            : Boolean((f.file_name ?? '').trim() || (f.reason ?? '').trim());
        })
        .map((f) => {
          const { fileName, originalFileName } = normalizeDocumentFileFields(
            f.file_name ?? '',
            f.original_file_name,
          );
          const typeId = apiDocumentTypeId(f);
          return {
            id: (f.case_request_document_id ?? '').trim() || crypto.randomUUID(),
            checklistItemId: '',
            documentTypeId: typeId,
            documentName: f.document_type_name?.trim() || 'Document',
            mandatory: false,
            fileName,
            originalFileName,
            uploadTime: f.upload_time ?? '',
            skipReason: f.reason ?? '',
          };
        });
    }
    return prev.documents.map((doc) => {
      const match = fromApi.find((f) => {
        const typeId = apiDocumentTypeId(f);
        if (typeId && typeId === doc.documentTypeId) return true;
        if (
          !isDocumentTypeUuid(doc.documentTypeId) &&
          f.document_type_name &&
          doc.documentName &&
          f.document_type_name.trim().toLowerCase() === doc.documentName.trim().toLowerCase()
        ) {
          return true;
        }
        return false;
      });
      if (!match) return doc;
      const { fileName, originalFileName } = normalizeDocumentFileFields(
        match.file_name ?? '',
        match.original_file_name,
      );
      return {
        ...doc,
        documentTypeId: apiDocumentTypeId(match) || doc.documentTypeId,
        fileName: fileName || doc.fileName,
        originalFileName: originalFileName || doc.originalFileName,
        uploadTime: match.upload_time ?? doc.uploadTime,
        skipReason: match.reason ?? doc.skipReason,
      };
    });
  })();

  const fileCategoryId = response.file_category_id ?? s1?.category_id ?? prev.fileCategoryId;
  const fileCategoryName =
    response.file_category_name ??
    s1?.category_name ??
    prev.fileCategory ??
    (fileCategoryId === AGD_INTERNAL_CATEGORY_ID
      ? FILE_CATEGORY_AGD_INTERNAL
      : FILE_CATEGORY_ATTORNEY_GENERAL);

  const partyBody = partyBodyTypeFromStep2(s2);
  const accused = accusedFromStep5(s5, prev.accused);

  const warrantAvailable =
    s7?.is_arrest_warrant_available ?? s7?.arrest_warrant_available ?? undefined;

  const numRespondents = s8?.number_of_respondents;
  const numberOfRespondents =
    numRespondents != null && String(numRespondents).trim() ? String(numRespondents) : prev.numberOfRespondents;

  return {
    ...prev,
    draftRequestId: caseRequestIdFromResponse(response) || prev.draftRequestId,
    referenceNo: referenceNoFromResponse(response) ?? prev.referenceNo,
    fileCategory: fileCategoryName,
    fileCategoryId: fileCategoryId || ATTORNEY_GENERAL_CATEGORY_ID,
    subFileCategoryId: response.file_sub_category_id ?? s1?.sub_category_id ?? prev.subFileCategoryId,
    subFileCategoryName: response.file_sub_category_name ?? s1?.sub_category_name ?? prev.subFileCategoryName,
    applicableAreaIds: resolveApplicableAreaIds(response, prev.applicableAreaIds),
    applicableAreas: resolveApplicableAreaNames(response, prev.applicableAreas),
    description: s1?.description ?? prev.description,
    provinceId: s1?.province_id ?? prev.provinceId,
    provinceName: s1?.province_name ?? prev.provinceName,
    districtId: s1?.district_id ?? prev.districtId,
    districtName: s1?.district_name ?? prev.districtName,
    caseSource: s1?.case_source ?? prev.caseSource,
    highCourtId: s1?.high_court_id ?? prev.highCourtId,
    highCourtName: s1?.high_court_name ?? prev.highCourtName,
    magistrateCourtId: s1?.magistrate_court_id ?? prev.magistrateCourtId,
    magistrateCourtName: s1?.magistrate_court_name ?? prev.magistrateCourtName,
    policeStationId: s1?.police_station_id ?? prev.policeStationId,
    policeStationName: s1?.police_station_name ?? prev.policeStationName,
    countryInvolvedId: s1?.country_involved_id ?? prev.countryInvolvedId,
    countryInvolvedName: s1?.country_involved_name ?? prev.countryInvolvedName,
    directionOfRequest: s1?.direction_of_request ?? prev.directionOfRequest,
    yourReferenceNumber: s1?.external_case_reference_number ?? prev.yourReferenceNumber,
    otherExternalReferences: s1?.other_external_case_reference_number ?? prev.otherExternalReferences,
    hasPreviousAdvice: boolToYesNo(s1?.has_previous_advice_received) || prev.hasPreviousAdvice,
    previousAdviceUploads: inlineUploadsFromApi(step1AdviceFiles(s1)) || prev.previousAdviceUploads,
    hasConnectedAdviceCase:
      boolToYesNo(s1?.has_connected_relevant_case ?? s1?.has_connected_advice_case) || prev.hasConnectedAdviceCase,
    connectedAdviceDetails:
      pickStr(s1?.connected_case_details, s1?.connected_advice_details) || prev.connectedAdviceDetails,
    hasActionInstituted: boolToYesNo(s1?.has_action_instituted) || prev.hasActionInstituted,
    courtTypeId: s1?.court_type_id ?? prev.courtTypeId,
    courtTypeName: s1?.court_type_name ?? prev.courtTypeName,
    courtTypeOther: s1?.other_court_type ?? prev.courtTypeOther,
    courtId: s1?.court_id ?? prev.courtId,
    courtName: s1?.court_name ?? prev.courtName,
    isAppealMatter: boolToYesNo(s1?.is_appeal_matter) || prev.isAppealMatter,
    originalCourt: s1?.original_court ?? prev.originalCourt,
    judgementOrderUploads: inlineUploadsFromApi(step1JudgementFiles(s1)) || prev.judgementOrderUploads,
    caseNumber: s1?.case_number ?? prev.caseNumber,
    hasConnectedMatters: boolToYesNo(s1?.has_connected_case) || prev.hasConnectedMatters,
    connectedCaseNumber: s1?.connected_case_number ?? prev.connectedCaseNumber,
    connectedAgdFileRef: s1?.connected_agd_file_ref_number ?? prev.connectedAgdFileRef,
    plaintiffName: s2?.plaintiff_applicant_name ?? prev.plaintiffName,
    defendantName: s2?.defendant_respondent_name ?? prev.defendantName,
    partyBodyType: partyBody || prev.partyBodyType || 'government',
    otherInvolvedInstitutions: s2?.other_involved_institutions ?? prev.otherInvolvedInstitutions,
    legalOfficerName:
      pickStr(s2?.leagel_office_or_officer_incharge_name, s2?.legal_office_or_officer_incharge_name) ||
      prev.legalOfficerName,
    legalOfficerContact:
      pickStr(s2?.leagel_office_or_officer_incharge_contact_no, s2?.legal_office_or_officer_incharge_contact_no) ||
      prev.legalOfficerContact,
    subjectOfDispute: s3?.subject_of_dispute ?? prev.subjectOfDispute,
    incidentDateFrom: s3?.incident_occurred_from_date ?? prev.incidentDateFrom,
    incidentDateTo: s3?.incident_occurred_to_date ?? prev.incidentDateTo,
    nextStepOfCaseId: s3?.case_next_step_id ?? prev.nextStepOfCaseId,
    nextStepOfCaseName: s3?.case_next_step_name ?? prev.nextStepOfCaseName,
    nextStepOfCaseOther: s3?.case_next_step_other ?? prev.nextStepOfCaseOther,
    nextCaseDate: s3?.case_next_date ?? prev.nextCaseDate,
    victimName: s4?.name_of_victim ?? prev.victimName,
    victimDob: pickStr(s4?.date_of_birth_victim, s4?.date_of_birth) || prev.victimDob,
    victimGenderId: pickStr(s4?.gender_id_victim, s4?.gender_id) || prev.victimGenderId,
    victimGenderName: pickStr(s4?.gender_name_victim, s4?.gender_name) || prev.victimGenderName,
    offenceDateFrom: s5?.date_of_offence_from ?? prev.offenceDateFrom,
    offenceDateTo: s5?.date_of_offence_to ?? prev.offenceDateTo,
    placeOfOffence: s5?.place_of_offence ?? prev.placeOfOffence,
    accused: accused.length ? accused : prev.accused,
    accusedName: pickStr(s6?.name_of_accused, s6?.accused_name) || prev.accusedName,
    nationalityId: pickStr(s6?.nationality_id_accused, s6?.nationality_id) || prev.nationalityId,
    nationalityName: pickStr(s6?.nationality_name_accused, s6?.nationality_name) || prev.nationalityName,
    countryOfOriginId: pickStr(s6?.country_of_origin_id_accused, s6?.country_of_origin_id) || prev.countryOfOriginId,
    countryOfOriginName:
      pickStr(s6?.country_of_origin_name_accused, s6?.country_of_origin_name) || prev.countryOfOriginName,
    accusedDob: pickStr(s6?.date_of_birth_accused, s6?.date_of_birth) || prev.accusedDob,
    accusedGenderId: pickStr(s6?.gender_id_accused, s6?.gender_id) || prev.accusedGenderId,
    accusedGenderName: pickStr(s6?.gender_name_accused, s6?.gender_name) || prev.accusedGenderName,
    passportOrIdNumber:
      pickStr(s6?.passport_or_id_number_accused, s6?.passport_or_id_number, s6?.passport_or_nic) ||
      prev.passportOrIdNumber,
    custodyStatusId:
      pickStr(s6?.current_custody_status_id_accused, s6?.current_custody_status_id) || prev.custodyStatusId,
    custodyStatusName:
      pickStr(s6?.current_custody_status_name_accused, s6?.current_custody_status_name) || prev.custodyStatusName,
    custodialAuthority:
      pickStr(s6?.custodial_authority_accused, s6?.custodial_authority) || prev.custodialAuthority,
    lastKnownLocation:
      pickStr(s6?.last_known_location_accused, s6?.last_known_location) || prev.lastKnownLocation,
    arrestWarrantAvailable: boolToYesNo(warrantAvailable) || prev.arrestWarrantAvailable,
    warrantReference: s7?.warrant_reference ?? prev.warrantReference,
    applicableTreatyArrangement:
      s7?.applicable_treaty_or_arrangement ?? s7?.applicable_treaty_arrangement ?? prev.applicableTreatyArrangement,
    petitionerName: pickStr(s8?.name_of_petitioner, s8?.petitioner_name) || prev.petitionerName,
    petitionerNic:
      pickStr(s8?.nic_or_identification_number_petitioner, s8?.petitioner_nic) || prev.petitionerNic,
    petitionerAddress: pickStr(s8?.address_petitioner, s8?.petitioner_address) || prev.petitionerAddress,
    petitionerContact: pickStr(s8?.contact_no_petitioner, s8?.petitioner_contact_no) || prev.petitionerContact,
    petitionerEmail: pickStr(s8?.email_address_petitioner, s8?.petitioner_email) || prev.petitionerEmail,
    representedByAttorney: boolToYesNo(s8?.is_represented_by_attorney) || prev.representedByAttorney,
    attorneyName: s8?.attorney_name ?? prev.attorneyName,
    attorneyContact:
      pickStr(s8?.attorney_contact_number, s8?.attorney_contact_no) || prev.attorneyContact,
    respondentNames: pickStr(s8?.respondent_names, s8?.respondent_name) || prev.respondentNames,
    respondentType: pickStr(s8?.respondent_type_id, s8?.respondent_type) || prev.respondentType,
    respondentTypeName: s8?.respondent_type_name ?? prev.respondentTypeName,
    respondentAddress: s8?.respondent_address ?? prev.respondentAddress,
    numberOfRespondents,
    rtiRequestorName: s9?.requestor_name ?? prev.rtiRequestorName,
    rtiNicOrPassport: s9?.nic_or_passport_number ?? prev.rtiNicOrPassport,
    rtiAddress: pickStr(s9?.requestor_address, s9?.address) || prev.rtiAddress,
    rtiContactNumber: pickStr(s9?.requestor_contact_number, s9?.contact_number) || prev.rtiContactNumber,
    rtiEmailAddress: pickStr(s9?.requestor_email_address, s9?.email_address) || prev.rtiEmailAddress,
    rtiIsSriLankanCitizen:
      boolToYesNo(s9?.is_requestor_sri_lankan ?? s9?.is_sri_lankan_citizen) || prev.rtiIsSriLankanCitizen,
    rtiSpecificPublicAuthorityName: s9?.specific_public_authority_name ?? prev.rtiSpecificPublicAuthorityName,
    rtiRequestedInformationDetails: s9?.requested_information_details ?? prev.rtiRequestedInformationDetails,
    rtiInformationRequestedTimePeriod:
      s9?.information_requested_time_period ?? prev.rtiInformationRequestedTimePeriod,
    rtiPreferredMethodOfAccessIds:
      s9?.preferred_method_of_access_ids?.length
        ? s9.preferred_method_of_access_ids.filter(Boolean)
        : prev.rtiPreferredMethodOfAccessIds,
    rtiPreferredMethodOfAccessNames:
      s9?.preferred_method_of_access_names?.length
        ? s9.preferred_method_of_access_names.filter(Boolean)
        : prev.rtiPreferredMethodOfAccessNames,
    rtiPreferredLanguageId: s9?.preferred_language_id ?? prev.rtiPreferredLanguageId,
    rtiPreferredLanguageName: s9?.preferred_language_name ?? prev.rtiPreferredLanguageName,
    rtiInvolvesLifeOrPersonalLiberty:
      boolToYesNo(s9?.involves_life_or_personal_liberty) || prev.rtiInvolvesLifeOrPersonalLiberty,
    rtiJustification: s9?.justification ?? prev.rtiJustification,
    rtiAdditionalNotes: s9?.additional_notes ?? prev.rtiAdditionalNotes,
    urgencyLevelId: s10?.urgency_level_id ?? prev.urgencyLevelId,
    urgencyLevelName: s10?.urgency_level_name ?? prev.urgencyLevelName,
    deadline: s10?.deadline ?? prev.deadline,
    urgencyJustification: s10?.urgency_justification ?? prev.urgencyJustification,
    documents,
    confirmCorrect: response.confirmed_correct ?? prev.confirmCorrect,
  };
}
